import React, { useState } from 'react';
import {
  Box,
  Typography,
  TableRow,
  TableCell,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  TextField,
  SxProps,
} from '@mui/material';
import {} from // Delete as DeleteIcon,  (removed)
'@mui/icons-material';
import { DataLandScape } from './EnterpriseContextInterfaces';
import { Theme } from '@emotion/react';

interface FunctionData {
  id: string;
  name: string;
  technologies: Array<{
    id: string;
    name: string;
    type: 'Core' | 'Other' | 'SAP Previous Versions' | 'Oracle Previous Versions';
  }>;
}

interface DataLandscapeRowProps {
  platform: DataLandScape;
  index: number;
  functions: FunctionData[];
  coreModuleProducts: string[];
  otherSoftwareOptions: string[];
  handleFieldChange: (index: number, field: string, value: string | string[]) => void;
  handleDeleteClick: (index: number) => void;
  isAutoFilled?: boolean;
}

const DataLandscapeRow: React.FC<DataLandscapeRowProps> = ({
  platform,
  index,
  functions,
  coreModuleProducts,
  otherSoftwareOptions,
  handleFieldChange,
  handleDeleteClick,
  isAutoFilled = false,
}) => {
  // State to track image loading errors for this row
  const [imageLoadError, setImageLoadError] = useState<string | null>(null);

  // Utility: get company logo filename from product name
  const getCompanyLogoSrc = (productName: string) => {
    if (!productName && typeof productName === 'string') return '';
    const allIcons = [
      'adobe.svg',
      'adp.svg',
      'anaplan.svg',
      'atlassian.svg',
      'blue-yonder.svg',
      'CCH Tagetik.svg',
      'coupa.svg',
      'epicor.svg',
      'Groove.svg',
      'hubspot.svg',
      'IBM.svg',
      'ifs.svg',
      'infor.svg',
      'kinaxis.svg',
      'mailchimp.svg',
      'microsoft.svg',
      'mitratech.svg',
      'onestream.svg',
      'oracle.svg',
      'Outreach.svg',
      'planful.svg',
      'pega.svg',
      'sage.svg',
      'Salesforce.svg',
      'SAP.svg',
      'service-now.svg',
      'siemens.svg',
      'syncron.svg',
      'snowflake.svg',
      'uipath.svg',
      'Veeva.svg',
      'workday.svg',
      'xero.svg',
      'zoho.svg',
    ];

    const words = (productName?.split(/\s+/) || [])?.map((w) =>
      w?.toLowerCase()?.replace(/[^a-z0-9]/g, '')
    );

    let found = '';

    for (const icon of allIcons) {
      const fileNoExt = icon
        .replace(/\.svg$/i, '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');

      // 1. Check for exact word match first
      if (words.includes(fileNoExt)) {
        found = icon;
        break;
      }
    }

    if (!found) {
      for (const icon of allIcons) {
        const fileNoExt = icon
          .replace(/\.svg$/i, '')
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '');

        // 2. Fallback to substring match
        if (words.some((w) => w && fileNoExt.includes(w))) {
          found = icon;
          break;
        }
      }
    }

    return found ? `/images/company/${found}` : '';
  };

  const autoFilledBorderSx: SxProps<Theme> = isAutoFilled
    ? {
        '& .MuiOutlinedInput-root': {
          position: 'relative',
          borderRadius: '10px',
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            padding: '1px',
            borderRadius: '10px',
            background: 'linear-gradient(90deg, #4479F4 0%, #41B474 50%, #4479F4 100%)',
            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
            pointerEvents: 'none',
          },
        },
      }
    : {};

  // Utility: generate initials from product name
  const getProductInitials = (productName: string): string => {
    if (!productName) return '';

    // Split by spaces, hyphens, underscores, and other common separators
    const words = productName
      .split(/[\s._-]+/)
      .filter((word) => word.length > 0)
      .map((word) => word.trim());

    if (words.length === 0) return '';

    // Take first character of first two words, or just first character if single word
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    } else {
      return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
    }
  };

  // Handle image load error
  const handleImageError = (productName: string = '') => {
    setImageLoadError(productName);
  };

  // Reset image error when product changes
  React.useEffect(() => {
    setImageLoadError(null);
  }, [platform.coreModuleProduct]);

  return (
    <TableRow
      sx={{
        '&:hover': {
          backgroundColor: '#f8f9fa',
        },
        '& td': {
          border: 'none',
        },
      }}
    >
      {/* Function Column */}
      <TableCell sx={{ py: 2, border: 'none' }}>
        {platform.function ? (
          <Typography
            sx={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 14, py: 1, paddingLeft: '0px' }}
          >
            {functions.find((f) => f.id === platform.function)?.name || platform.function}
          </Typography>
        ) : (
          <FormControl fullWidth size="small" sx={{ ...autoFilledBorderSx }}>
            <Select
              value={platform.function || ''}
              onChange={(e) => handleFieldChange(index, 'function', e.target.value)}
              displayEmpty
              sx={{
                backgroundColor: 'white',
                fontSize: 14,
                fontFamily: 'Poppins',
                fontWeight: 600,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#e0e0e0',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2196f3',
                },
                '& .MuiSelect-select': {
                  py: 1,
                  paddingLeft: '15px',
                },
              }}
              inputProps={{
                sx: {
                  padding: '9px 32px 9px 10px',
                  minHeight: 42,
                },
              }}
            >
              {functions.map((func) => (
                <MenuItem key={func.id} value={func.id}>
                  {func.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </TableCell>

      {/* Core Module Product Name Column */}
      <TableCell sx={{ py: 2, border: 'none' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl
            size="small"
            sx={{ background: '#fff', width: '350px', ...autoFilledBorderSx }}
          >
            <Select
              value={platform.coreModuleProduct || ''}
              onChange={(e) => handleFieldChange(index, 'coreModuleProduct', e.target.value)}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Typography color="textSecondary" sx={{ fontFamily: 'Poppins', fontSize: 16 }}>
                      Select Core Product
                    </Typography>
                  );
                }
                return selected;
              }}
              sx={{
                fontSize: 16,
                fontFamily: 'Poppins',
                fontWeight: 400,
                width: '350px',
                // minWidth: 350,
                lineHeight: '20px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#e0e0e0',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2196f3',
                },
                '& .MuiSelect-select': {
                  py: 1,
                  paddingLeft: '15px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
              inputProps={{
                sx: {
                  padding: '9px 32px 9px 10px',
                  minHeight: 42,
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    fontSize: 16,
                    fontFamily: 'Poppins',
                    borderRadius: 2,
                    maxHeight: 300,
                    minWidth: 'unset',
                  },
                },
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
              }}
            >
              {coreModuleProducts.map((product) => (
                <MenuItem
                  key={product}
                  value={product}
                  sx={{ fontFamily: 'Poppins', fontSize: 16 }}
                >
                  {product}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Icon/Image Display field styled like text fields */}
          <Box
            sx={{
              width: 48,
              height: 42,
              background: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ml: 1,
            }}
          >
            {platform.coreModuleProduct && (
              <>
                {/* Try to show company logo first */}
                {getCompanyLogoSrc(platform.coreModuleProduct) &&
                imageLoadError !== platform.coreModuleProduct ? (
                  <img
                    key={`${platform.coreModuleProduct}-${index}`}
                    src={getCompanyLogoSrc(platform.coreModuleProduct)}
                    alt={platform.coreModuleProduct + ' icon'}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '6px',
                    }}
                    onError={() => handleImageError(platform.coreModuleProduct)}
                    onLoad={() => setImageLoadError(null)}
                  />
                ) : (
                  /* Fallback to initials */
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(135deg, #BBDBFF 0%, #51A3FF 100%)',
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: '500',
                      fontFamily: 'Poppins',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {getProductInitials(platform.coreModuleProduct)}
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </TableCell>

      {/* Other Software Column */}
      <TableCell sx={{ py: 2, border: 'none' }}>
        <FormControl fullWidth size="small" sx={{ ...autoFilledBorderSx }}>
          <Select
            value={platform.otherSoftware || ''}
            onChange={(e) => handleFieldChange(index, 'otherSoftware', e.target.value)}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <Typography color="textSecondary" sx={{ fontFamily: 'Poppins', fontSize: 16 }}>
                    Select Other Software
                  </Typography>
                );
              }
              return selected;
            }}
            sx={{
              fontSize: 16,
              fontFamily: 'Poppins',
              fontWeight: 400,
              width: '350px',
              lineHeight: '20px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e0e0e0',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#2196f3',
              },
              '& .MuiSelect-select': {
                py: 1,
                paddingLeft: '15px',
              },
            }}
            inputProps={{
              sx: {
                padding: '9px 32px 9px 10px',
                minHeight: 42,
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  fontSize: 16,
                  fontFamily: 'Poppins',
                  borderRadius: 2,
                  maxHeight: 300,
                  minWidth: 'unset',
                },
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            }}
          >
            {otherSoftwareOptions.map((software) => (
              <MenuItem
                key={software}
                value={software}
                sx={{ fontFamily: 'Poppins', fontSize: 16 }}
              >
                {software}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </TableCell>

      {/* Other Technology Vendors Column */}
      <TableCell sx={{ py: 2, border: 'none' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Example Vendor"
          value={
            platform.otherTechnologyVendorsInput ??
            (platform.otherTechnologyVendors || []).join(', ')
          }
          onChange={(e) => {
            const raw = e.target.value;
            handleFieldChange(index, 'otherTechnologyVendorsInput', raw);
            const vendors =
              raw.trim() === ''
                ? []
                : raw
                    .split(',')
                    .map((v) => v.trim())
                    .filter((v) => v.length > 0);

            handleFieldChange(index, 'otherTechnologyVendors', vendors);
          }}
          sx={{
            backgroundColor: 'white',
            '& .MuiOutlinedInput-root': {
              fontFamily: 'Poppins',
              fontSize: 14,
              '& fieldset': {
                borderColor: '#e0e0e0',
              },
              '&:hover fieldset': {
                borderColor: '#2196f3',
              },
              '& input': {
                py: 1,
              },
            },
            ...autoFilledBorderSx,
          }}
        />
      </TableCell>

      {/* Delete Button Column */}
      <TableCell sx={{ py: 2, textAlign: 'center', border: 'none' }}>
        <IconButton
          onClick={() => handleDeleteClick(index)}
          size="small"
          sx={{
            color: '#f44336',
            backgroundColor: '#FFECEA',
            width: 32,
            height: 32,
            borderRadius: 1, // Changed from '50%' or default to make it square
            '&:hover': {
              backgroundColor: '#ffcdd2',
            },
          }}
        >
          <img src="/images/deleteEnterprise.svg" alt="Delete" style={{ width: 20, height: 20 }} />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

export default DataLandscapeRow;
